import { Button, ButtonTheme } from 'components/Button';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { NavigationHeader } from 'components/NavigationHeader';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { SortMenu } from 'components/SortMenu';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { DOWNLOAD_ASSETS, SET_FILTER, SET_SORT } from 'store/actions';
import {
  selectChosenSearchAssets,
  selectChosenSearchAssetsCount,
  selectSearchOptionsSort,
  selectSearchOrganisationFilterDisplay,
  selectSearchOrganisationFilterOptions,
  selectSearchSortValue,
  selectSearchTextFilter,
} from 'store/search/selectors';

type Props = {
  openOrganisationFilter: () => void;
  onClose: () => void;
};

export const CloudAssetsHeader: React.FC<Props> = ({
  onClose,
  openOrganisationFilter,
}) => {
  const dispatch = useDispatch();

  const chosenAssetsCount = useSelector(selectChosenSearchAssetsCount);
  const chosen = useSelector(selectChosenSearchAssets);
  const searchTextFilter = useSelector(selectSearchTextFilter);

  const searchOptionsSort = useSelector(selectSearchOptionsSort);

  const searchSortValue = useSelector(selectSearchSortValue);

  const cloudOrganisationFilterDisplay = useSelector(
    selectSearchOrganisationFilterDisplay,
  );
  const searchOrganisationFilterOptions = useSelector(
    selectSearchOrganisationFilterOptions,
  );

  const handleDownload = () => dispatch(DOWNLOAD_ASSETS(chosen));

  const handleSearch = React.useCallback(
    (search: string) => dispatch(SET_FILTER({ search })),
    [dispatch],
  );

  const handleChangeSort = (id: string) => {
    dispatch(SET_SORT(id));
  };

  return (
    <>
      {chosenAssetsCount === 0 ? (
        <NavigationHeader
          heading="Download Assets"
          onPressSaveButton={onClose}
          saveButtonText="Done"
          onPressCancelButton={onClose}
          cancelButtonText="Close"
        />
      ) : (
        <NavigationHeader
          heading={pluralise(chosenAssetsCount, '1 Asset', '{x} Assets')}
          onPressCancelButton={onClose}
          buttons={
            <Button onPress={handleDownload} text="Download" marginLeft />
          }
        />
      )}
      <View
        style={
          chosenAssetsCount !== 0
            ? {
                opacity: 0.42,
                pointerEvents: 'none',
              }
            : undefined
        }
      >
        <NavigationHeader
          contentStyle={{
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            paddingBottom: theme.paddingHorizontal,
          }}
          marginLeft={0}
          marginRight={0}
        >
          <TextField
            value={searchTextFilter}
            iconLeft={ICONS.MagnifyingGlassLight}
            onChange={handleSearch}
            placeholder="Search Cloud Assets"
            clearable
            onChangeDebounceMs={300}
            containerStyle={{
              backgroundColor: '#ffffff',
              borderRadius: 4,
              flex: 1,
              height: 40,
              overflow: 'hidden',
            }}
            onEsc={onClose}
            autoFocusWeb
            autoFocusDelay
            autoCorrect={false}
            autoComplete="off"
            autoCapitalize="none"
          />
        </NavigationHeader>
        <SeparatorHorizontal />
        <View
          style={{
            flexDirection: 'row',
            paddingHorizontal: theme.paddingHorizontal,
            paddingVertical: theme.paddingHorizontal,
          }}
        >
          {searchOrganisationFilterOptions.length > 1 && (
            <Button
              chipLike
              textSize="small"
              style={{ maxWidth: 150 }}
              marginRight
              text={cloudOrganisationFilterDisplay || 'Organisations'}
              buttonTheme={
                cloudOrganisationFilterDisplay
                  ? ButtonTheme.primary
                  : ButtonTheme.themeless
              }
              onPress={openOrganisationFilter}
            />
          )}
          <SortMenu
            value={searchSortValue}
            options={searchOptionsSort}
            onChange={handleChangeSort}
          />
        </View>
      </View>
    </>
  );
};
